import { useReactQuery } from '@flik/shared/api';
import { useTheme } from '@flik/shared/components';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const ProfileSetupPage: React.FC = () => {
  const { provideTailwind } = useTheme();
  const navigate = useNavigate();
  const apiClient = useReactQuery();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    nickname: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Create mutation hook for the create-user API
  const createUser = apiClient.useMutation('post', '/v1/create-user');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (error) {
      setError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      setError('First name and last name are required.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await createUser.mutateAsync({
        body: {
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          nickname: formData.nickname.trim() || null,
          profilePictureUrl: null, // Ignoring for now as requested
        },
      });

      if (response.user) {
        // Profile created successfully, force a full page refresh to clear caches
        console.log(
          'ProfileSetupPage: Profile created successfully, refreshing page',
        );

        // Force a full page refresh to clear all caches and get fresh data
        window.location.href = '/';
      } else {
        console.log(
          'ProfileSetupPage: Profile creation failed - no user in response',
        );
        setError('Failed to create profile. Please try again.');
      }
    } catch (err) {
      console.error('Error creating user profile:', err);
      setError('Failed to create profile. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`w-full min-h-screen ${provideTailwind('bg-primary')}`}>
      <div className="max-w-md mx-auto px-6 py-20">
        <div className="text-center mb-12">
          <h1
            className={`text-4xl font-bold mb-3 ${provideTailwind(
              'text-primary',
            )}`}
          >
            Set up your profile
          </h1>
          <p className={`text-base ${provideTailwind('text-secondary')}`}>
            Complete your profile to continue using Flik
          </p>
        </div>

        <div className="space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm dark:bg-red-900/20 dark:border-red-800 dark:text-red-200">
              {error}
            </div>
          )}

          <form className="space-y-4" onSubmit={handleSubmit}>
            <div>
              <label
                htmlFor="firstName"
                className={`block text-sm font-medium mb-2 ${provideTailwind(
                  'text-primary',
                )}`}
              >
                First Name *
              </label>
              <input
                id="firstName"
                name="firstName"
                type="text"
                required
                className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-600 dark:text-white dark:placeholder-gray-400 transition-colors"
                placeholder="Enter your first name"
                value={formData.firstName}
                onChange={handleInputChange}
                disabled={isLoading}
              />
            </div>

            <div>
              <label
                htmlFor="lastName"
                className={`block text-sm font-medium mb-2 ${provideTailwind(
                  'text-primary',
                )}`}
              >
                Last Name *
              </label>
              <input
                id="lastName"
                name="lastName"
                type="text"
                required
                className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-600 dark:text-white dark:placeholder-gray-400 transition-colors"
                placeholder="Enter your last name"
                value={formData.lastName}
                onChange={handleInputChange}
                disabled={isLoading}
              />
            </div>

            <div>
              <label
                htmlFor="nickname"
                className={`block text-sm font-medium mb-2 ${provideTailwind(
                  'text-primary',
                )}`}
              >
                Nickname (Optional)
              </label>
              <input
                id="nickname"
                name="nickname"
                type="text"
                className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-600 dark:text-white dark:placeholder-gray-400 transition-colors"
                placeholder="Enter a nickname (optional)"
                value={formData.nickname}
                onChange={handleInputChange}
                disabled={isLoading}
              />
            </div>

            <button
              type="submit"
              disabled={
                isLoading ||
                !formData.firstName.trim() ||
                !formData.lastName.trim()
              }
              className="w-full py-4 px-4 bg-pink-600 text-white text-lg font-medium rounded-lg hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
            >
              {isLoading ? (
                <svg
                  className="animate-spin h-5 w-5 text-white mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              ) : null}
              Complete Profile
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProfileSetupPage;
